import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { RootState } from "../../app/store";
import { getBackendUrl, LoadStateInfo, loaded, loading, notStarted, error, failedResponse, LoadState } from "../common";
import { getToken } from "../../features/user/UserAvatar";

export interface HomeworkState {
    state: LoadStateInfo;
    request?: RetrieveHomeworkRequest;
    selected?: HomeworkInfo;
}

const initialState: HomeworkState = {
    state: notStarted(),
};

export interface RetrieveHomeworkRequest {
    homeworkId: string;
}

export const retrieveHomework = createAsyncThunk(
    "retrieveHomework",
    async (param: RetrieveHomeworkRequest, thunkAPI) => {
        console.debug("retrieve homework");

        const response = await fetch(`${getBackendUrl()}/api/homework/${param.homeworkId}`, {
            signal: thunkAPI.signal,
            headers: {
                "Authorization": `Bearer ${getToken()}`,
            }
        });

        if (response.ok) {
            return await response.json();
        } else {
            return thunkAPI.rejectWithValue(failedResponse(response));
        }
    },
    {
        condition: (param: RetrieveHomeworkRequest, thunkAPI) => {
            const state = thunkAPI.getState() as RootState;
            const status = state.homework;
            if (!status || !status.state) {
                return true;
            }
            if (status.state.state == LoadState.NotStarted) {
                return true;
            }
            console.debug(`retrieveHomework skipped: ${status.state.state}`);
            return false;
        }
    }
);

export const homeworkSlice = createSlice({
    name: "homework",
    initialState,
    // The `reducers` field lets us define reducers and generate associated actions
    reducers: {
        select: (state, action: PayloadAction<HomeworkInfo | undefined>) => {
            console.trace(`select :: ${action.payload?.name}`);
            state.selected = action.payload;
        },
        check: (state, action: PayloadAction<string | undefined>) => {
            if (state.state.state != LoadState.NotStarted
                && (action.payload == undefined || state.request?.homeworkId != action.payload)
            ) {
                state.request = undefined;
                state.state = notStarted();
                state.selected = undefined;
            }
        },
        clean: (state) => {
            state.request = undefined;
            state.selected = undefined;
        }
    },
    // The `extraReducers` field lets the slice handle actions defined elsewhere,
    // including actions generated by createAsyncThunk or in other slices.
    extraReducers: (builder) => {
        builder
            .addCase(retrieveHomework.pending, (state, action) => {
                state.request = action.meta.arg;
                state.state = loading();
                state.selected = undefined;
            })
            .addCase(retrieveHomework.fulfilled, (state, action) => {
                console.debug(`${action.type}`);
                state.state = loaded();
                state.selected = action.payload;
            })
            .addCase(retrieveHomework.rejected, (state, action) => {
                state.state = error(action);
            })
            ;
    },
});

export enum HomeworkType {
    StudentCheck = 0,
    TeacherReviewed = 1,
}

export interface HomeworkInfo {
    id: number;
    name: string;
    description: string;
    schoolId: number;
    classroomId: number;
    startDate: string;
    dueDate: string;
    homeworkType: HomeworkType;
    createdBy: number;
    updatedBy: number;
}

export const selectHomework = (state: RootState) => state.homework;

export default homeworkSlice.reducer;
